import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { PriorityBadge, StatusBadge } from "@/components/Badges";
import { PRIORITIES, STATUS_ORDER, VEHICLE_STATUSES, timeAgo, fmtMinutes, rangeFor, fmtRangeLabel } from "@/lib/constants";
import { Siren, CheckCircle2, Truck, Clock, CalendarDays, Timer, Building2, ArrowUpRight, Plus } from "lucide-react";

function Kpi({ icon: Icon, label, value, sub, accent = "#2563eb", testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-900">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: `${accent}18`, color: accent }}>
          <Icon style={{ width: 20, height: 20 }} />
        </div>
      </div>
    </div>
  );
}

const RANGE_MODES = [
  { key: "dienst" },
  { key: "week" },
  { key: "maand" },
  { key: "custom" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [kpis, setKpis] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [shift, setShift] = useState({ start: "08:00", end: "08:00" });
  const [mode, setMode] = useState("dienst");
  const [custom, setCustom] = useState({ from: "", to: "" });

  useEffect(() => { api.get("/settings/shift").then(({ data }) => setShift(data)); }, []);

  const load = useCallback(async () => {
    const [a, b] = rangeFor(mode, shift, custom);
    const params = { from: a.toISOString(), to: b.toISOString() };
    const [k, i, v] = await Promise.all([
      api.get("/dashboard/kpis", { params }),
      api.get("/incidents?status=open"),
      api.get("/vehicles"),
    ]);
    setKpis(k.data);
    setIncidents(i.data);
    setVehicles(v.data);
  }, [mode, shift, custom]);

  useEffect(() => { load(); }, [load]);

  const [flash, setFlash] = useState({});
  const clearFlash = useCallback((id) => setFlash((p) => { const n = { ...p }; delete n[id]; return n; }), []);
  useRealtime((msg) => {
    if (msg.type === "__ping") return;
    load();
    if (msg.type === "note_added" && msg.payload?.author_role === "voertuig" && msg.payload?.incident_id) {
      setFlash((p) => ({ ...p, [msg.payload.incident_id]: `Nieuwe kladblokregel · ${msg.payload.author || "voertuig"}` }));
    }
    if (msg.type === "vehicle_updated" && msg.payload?.id) {
      const lbl = `Status ${msg.payload.callsign || "voertuig"} → ${VEHICLE_STATUSES[msg.payload.status]?.label || msg.payload.status}`;
      setFlash((p) => { const n = { ...p }; incidents.filter((i) => i.vehicles?.some((v) => v.id === msg.payload.id)).forEach((i) => { n[i.id] = lbl; }); return n; });
    }
  });

  const byStatus = STATUS_ORDER.map((s) => ({ s, count: vehicles.filter((v) => v.status === s).length }));
  const periodLabel = t(`dash.period.${mode}`);
  const rangeReady = mode !== "custom" || (custom.from && custom.to);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <button data-testid="dashboard-new-incident" onClick={() => navigate("/incidenten", { state: { create: true } })}
        title={t("dash.newIncidentTitle")}
        className="no-print fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-blue-600 px-5 py-4 text-sm font-bold text-white shadow-lg shadow-blue-600/30 transition-transform hover:scale-105 active:scale-95">
        <Plus className="h-6 w-6" /> {t("dash.newIncident")}
      </button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("dash.title")}</h1>
          <p className="text-sm text-slate-500">{t("dash.overview")} · {rangeReady ? fmtRangeLabel(mode, shift, custom) : t("dash.pickPeriod")}</p>
        </div>
      </div>

      {/* Range selector */}
      <div className="flex flex-wrap items-center gap-2" data-testid="range-selector">
        {RANGE_MODES.map((r) => (
          <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setMode(r.key)}
            className={`rounded-xl px-3.5 py-2 text-sm font-medium transition-colors ${mode === r.key ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {t(`dash.ranges.${r.key}`)}
          </button>
        ))}
        {mode === "custom" && (
          <div className="flex items-center gap-2">
            <input data-testid="range-from" type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            <span className="text-slate-400">{t("dash.to")}</span>
            <input data-testid="range-to" type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi testid="kpi-open" icon={Siren} label={t("dash.kpi.open")} value={kpis?.open ?? "—"} accent="#dc2626" />
        <Kpi testid="kpi-closed" icon={CheckCircle2} label={t("dash.kpi.closed")} value={kpis?.closed ?? "—"} accent="#059669" />
        <Kpi testid="kpi-active-vehicles" icon={Truck} label={t("dash.kpi.activeVehicles")} value={kpis ? `${kpis.active_vehicles}/${kpis.total_vehicles}` : "—"} accent="#2563eb" />
        <Kpi testid="kpi-period" icon={CalendarDays} label={`${t("dash.kpi.incidents")} · ${periodLabel}`} value={kpis?.period ?? "—"} accent="#9333ea" />
        <Kpi testid="kpi-avg-close" icon={Clock} label={t("dash.kpi.avgClose")} value={kpis ? fmtMinutes(kpis.avg_close_min) : "—"} accent="#d97706" />
        <Kpi testid="kpi-avg-response" icon={Timer} label={t("dash.kpi.avgResponse")} value={kpis ? fmtMinutes(kpis.avg_response_min) : "—"} accent="#0891b2" />
      </div>

      {/* Priority breakdown */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="priority-breakdown">
        <div className="mb-3 text-sm font-semibold text-slate-700">{t("dash.byPriority")}</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[1, 2, 3, 4].map((p) => (
            <div key={p} className="rounded-xl p-3" style={{ backgroundColor: PRIORITIES[p].bg, border: `1px solid ${PRIORITIES[p].border}` }}>
              <div className="font-display text-2xl font-bold" style={{ color: PRIORITIES[p].text }}>{kpis?.by_priority?.[p] ?? 0}</div>
              <div className="text-xs font-semibold" style={{ color: PRIORITIES[p].text }}>{PRIORITIES[p].label} · P{p}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Active incidents board */}
        <div className="lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">{t("dash.activeIncidents")}</h2>
            <button data-testid="goto-incidents" onClick={() => navigate("/incidenten")} className="text-sm font-medium text-blue-600 hover:underline">
              {t("dash.viewAll")}
            </button>
          </div>
          <div className="space-y-3">
            {incidents.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">
                {t("dash.noOpen")}
              </div>
            )}
            {incidents.map((inc) => (
              <button
                key={inc.id}
                data-testid={`incident-card-${inc.id}`}
                onClick={() => { clearFlash(inc.id); navigate(`/incidenten/${inc.id}`); }}
                className={`group flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:shadow-md ${flash[inc.id] ? "flash-attention" : ""}`}
              >
                <div className="h-full w-1 shrink-0 self-stretch rounded-full" style={{ backgroundColor: PRIORITIES[inc.priority].hex }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority={inc.priority} />
                    <span className="font-mono text-xs text-slate-400">{inc.ref}</span>
                  </div>
                  <div className="mt-1 truncate font-semibold text-slate-900">{inc.title}</div>
                  {flash[inc.id] && <div data-testid={`flash-info-${inc.id}`} className="mt-1 inline-block rounded bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white">{flash[inc.id]}</div>}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Building2 style={{ width: 13, height: 13 }} />{inc.building?.name || "—"}</span>
                    <span>{inc.incident_type?.name}</span>
                    <span className="inline-flex items-center gap-1"><Truck style={{ width: 13, height: 13 }} />{t("dash.vehiclesCount", { count: inc.vehicles?.length || 0 })}</span>
                    <span>{timeAgo(inc.created_at)}</span>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-blue-600" />
              </button>
            ))}
          </div>
        </div>

        {/* Vehicle status board */}
        <div>
          <h2 className="mb-3 font-display text-lg font-bold text-slate-900">{t("dash.vehicleStatus")}</h2>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 grid grid-cols-5 gap-1.5">
              {byStatus.map(({ s, count }) => (
                <div key={s} className="rounded-lg p-2 text-center" style={{ backgroundColor: VEHICLE_STATUSES[s].bg }}>
                  <div className="font-display text-lg font-bold" style={{ color: VEHICLE_STATUSES[s].text }}>{count}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`vehicle-row-${v.id}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <div>
                    <div className="font-mono text-sm font-semibold text-slate-900">{v.callsign}</div>
                    <div className="text-xs text-slate-500">{v.name}</div>
                  </div>
                  <StatusBadge status={v.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
